const BASE_WP = import.meta.env.VITE_WP_BASE_URL;
const BASE_URL = `${BASE_WP}/wp-json/wp/v2`;
import { ICMSImage, CommonData } from '@/types/index';

export enum ItemType {
    PLUGIN = 42,
    BUNDLE = 44,
    SAMPLEPACK = 43,
}

export interface ICMSItem {
    showcaseId: number;
    editorId: number;
    name: string;
    shopDesc: string;
    itemType: ItemType;
    productPageSlug: string;
    shortDesc: string;
}

export interface IGroupedCMSItems {
    samplepack: ICMSItem[];
    plugin: ICMSItem[];
    bundle: ICMSItem[];
}

const CACHE_NAME = "cms-image-data-cache"; // For caching image data
const EXPIRATION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export class CMSClient {

  private static instance: CMSClient;

  private constructor() {
    // private to prevent outside instantiation
  }

  public static getInstance(): CMSClient {
      if (!CMSClient.instance) {
        CMSClient.instance = new CMSClient();
      }
      return CMSClient.instance;
   }

  private async fetchResource(endpoint: string): Promise<any> {
    const response = await fetch(`${BASE_URL}/${endpoint}`);

    if (!response.ok) {
      const responseData = await response.json();
      throw new Error(responseData.message || `HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  }

  // Fetch a specific post by ID
  public  async fetchPost(postId: number): Promise<any> {
    return this.fetchResource(`posts/${postId}`);
  }

  // Fetch all posts
  public  async fetchAllPosts(): Promise<any[]> {
    return this.fetchResource('posts');
  }

  // Fetch posts of a certain category by category ID
  public  async fetchPostsByCategory(categoryId: number): Promise<any[]> {
    return this.fetchResource(`posts?categories=${categoryId}`);
  }

  // Method to fetch a subcategory by its slug
  public async fetchSubcategoryBySlug(slug: string): Promise<any> {
    const response = await this.fetchResource(`categories?slug=${slug}`);
    // Since slugs are unique, the response should have at most one category
    return response.length > 0 ? response[0] : null;
  }

  public async fetchSubcategoryById(categoryId: number): Promise<any> {
    const response = await this.fetchResource(`categories/${categoryId}`);
    return response; // Directly return the response as ID should uniquely identify a category
  }

  // Method to fetch a post by its slug
  public async fetchPostBySlug(slug: string): Promise<any> {
    const response = await this.fetchResource(`posts?slug=${slug}`);
    if(response)
      console.log("Successfully got slug (adf): ", slug);
    else
      console.log("Failed to get slug (adf): ", slug);
    // Since slugs are unique, the response should have at most one post
    return response.length > 0 ? response[0] : null;
  }

  // Method to fetch subcategories of a given category
  public async fetchSubcategories(parentCategoryId: number): Promise<any[]> {
    return this.fetchResource(`categories?parent=${parentCategoryId}`);
  }

  // Fetch category information by category ID
  public  async fetchCategoryInfo(categoryId: number): Promise<any> {
    return this.fetchResource(`categories/${categoryId}`);
  }

  public async fetchImage(imageId: number): Promise<ICMSImage> {
        if (imageId === null || imageId === 0 || imageId === undefined) return null;
    
        // Try to fetch from the cache first
        const cachedData = await this.getCachedData(`media/${imageId}`);
        if (cachedData) {
            console.log(`Image data for ID ${imageId} found in cache.`);
            return cachedData as ICMSImage; // Convert the cached data back to ICMSImage type
        }
    
        // If not in cache, fetch from the server
        const rawData = await this.fetchResource(`media/${imageId}`);
    
        // Check if the rawData is undefined or null
        if (!rawData) {
            console.warn(`Warning: Image data for ID ${imageId} is ${rawData}.`);
            return null; // Or you can throw an error or provide a default image structure
        }
    
        // Process the rawData to fit the ICMSImage structure
        const cmsImage: ICMSImage = {
            resolvedImageSrc: rawData.guid?.rendered || null,
            computedSrcset: this.getComputedSrcset(rawData.media_details?.sizes),
            sizes: null, // Note: WordPress response doesn't seem to provide a direct equivalent. Adjust if you have a mapping.
            imageAlt: rawData.alt_text,
            imageTitle: rawData.title?.rendered || ""
        };
    
        // Cache the response for subsequent requests
        await this.setCacheData(`media/${imageId}`, cmsImage);
        console.log(`Image data for ID ${imageId} newly fetched.`);
        return cmsImage;
    }
    
    private async setCacheData(url: string, data: any): Promise<void> {
        const cache = await caches.open(CACHE_NAME);
        const cacheData = {
            data,
            timestamp: Date.now()
        };
        const response = new Response(JSON.stringify(cacheData), {
            headers: { 'Content-Type': 'application/json' }
        });
        await cache.put(url, response);
    }
    
    private async getCachedData(url: string): Promise<any> {
        const cache = await caches.open(CACHE_NAME);
        const cachedResponse = await cache.match(url);
        if (cachedResponse) {
            const cacheData = await cachedResponse.json();
            if (Date.now() - cacheData.timestamp < EXPIRATION_DURATION) {
                return cacheData.data;
            } else {
                console.log(`Cached data for ${url} has expired. Fetching new data.`);
                await cache.delete(url); // Optional: delete expired data from cache
            }
        }
        return null;
    }
    
    public async fetchItems(): Promise<IGroupedCMSItems> {
      try {
          const rawData = await this.fetchResource("item?per_page=100");
          console.log("rawData:", rawData);
          
          const groupedData: IGroupedCMSItems = {
              samplepack: [],
              plugin: [],
              bundle: []
          };
  
          rawData.forEach((item: any) => {
              // Check if itemType array has more than one value
              if (item.item_type.length > 1) {
                  console.warn(`Warning: Item with ID ${item.id} has multiple item types. Using only the first one.`);
              }
  
              const transformedItem: ICMSItem = {
                  wpId: item.id,
                  slug: item.slug,
                  showcaseId: item.showcase_image || null,
                  editorId: item.editor_image || null,
                  name: item.title.rendered,
                  shopDesc: item.shop_description,
                  itemType: item.item_type[0] as ItemType, // Using only the first value and casting to ItemType enum
                  shortDesc: item.short_description,
                  productPageSlug: item.product_page_slug,
                  featuredMedia: item.featured_media,
              };
  
              // Add the transformed item to the appropriate category
              switch(transformedItem.itemType) {
                  case ItemType.PLUGIN:
                      groupedData.plugin.push(transformedItem);
                      break;
                  case ItemType.BUNDLE:
                      groupedData.bundle.push(transformedItem);
                      break;
                  case ItemType.SAMPLEPACK:
                      groupedData.samplepack.push(transformedItem);
                      break;
              }
          });
          console.log("groupedData:", groupedData);
          return groupedData;
  
      } catch (error) {
          throw new Error(`Failed to fetch items: ${error.message}`);
      }
  }

  public async fetchItem(itemSlug: string): Promise<ICMSItem | null> {
    console.log("(adf) Trying to fetch item with slug: ", itemSlug);
    try {
      const rawData = await this.fetchResource("item?per_page=100");
  
      for (const item of rawData) {
        // Transform the item
        const transformedItem: ICMSItem = {
          wpId: item.id,
          slug: item.slug,
          showcaseId: item.showcase_image || null,
          editorId: item.editor_image || null,
          name: item.title.rendered,
          shopDesc: item.shop_description,
          productPageDesc: item.content.rendered,
          itemType: item.item_type[0] as ItemType, // Using only the first value and casting to ItemType enum
          shortDesc: item.short_description,
          productPageSlug: item.product_page_slug,
          featuredMedia: item.featured_media,
          wpStatus: item.status,
          yoastSEO: item.yoast_head_json || {} // Assuming yoast_head_json contains the structured SEO data
        };
  
        // Check if the item matches the slug
        if (transformedItem.slug === itemSlug) {
          console.log("(adf) Found product with with slug: ", itemSlug);
          return transformedItem;
        }
      }
  
      // If no item was found, return null or throw an error based on your error handling strategy
      console.warn(`No item found with the slug: ${itemSlug}`);
      return null;
  
    } catch (error) {
      throw new Error(`Failed to fetch item: ${error.message}`);
    }
  } 


    public async fetchCommonData(): Promise<CommonData> {
      try {
        const rawData = await this.fetchResource("common_data");
        
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
          throw new Error("No data found for common_data");
        }
        
        const firstItem = rawData[0];
        const welcomeText = firstItem.welcome_text; // Adjust this if the field key is different
    
        if (!welcomeText) {
          throw new Error("welcome_text not found in the first common_data item");
        }
    
        return {
          welcomeText: welcomeText
        };
    
      } catch (error) {
        throw new Error(`Failed to fetch common data: ${error.message}`);
      }
    }

    // Helper method to compute the srcset based on the image sizes
  private getComputedSrcset(sizes?: any): string | null {
      if (sizes) {
          return Object.keys(sizes)
             .reduce((srcset: string[], sizeKey: string) => {
                 srcset.push(`${sizes[sizeKey].source_url} ${sizes[sizeKey].width}w`);
                return srcset;
              }, [])
              .join(', ');
      }
    return null;
  }

  // Fetch a specific custom post type item by ID
  public  async fetchCustomPostTypeItem(customPostTypeSlug: string, itemId: number): Promise<any> {
    return this.fetchResource(`${customPostTypeSlug}/${itemId}`);
  }

  // Fetch all items of a specific custom post type
  public  async fetchAllCustomPostTypeItems(customPostTypeSlug: string): Promise<any[]> {
    return this.fetchResource(customPostTypeSlug);
  }
}

// USAGE EXAMPLE:

// Fetch posts of a certain category
// CMSClient.fetchPostsByCategory(12).then(posts => console.log(posts));

// Fetch category information
// CMSClient.fetchCategoryInfo(12).then(category => console.log(category));
